/**
 * CustomersContent - Gestão de clientes (CRM), importação CSV/XLSX e campanhas
 * Extraído VERBATIM do App.js (Fase 2 da refatoração — zero mudança de comportamento).
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Clock, DollarSign, Download, FileText, MoreVertical, Search, Send, Settings, ShoppingCart, Upload, User } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import CampaignsPanel from '../CampaignsPanel';
import { getBackendUrl } from '../../utils/api';

const API = `${getBackendUrl()}/api`;

const CustomersContent = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Auto Messages State
  const [activeTab, setActiveTab] = useState('customers'); // 'customers', 'auto-messages', 'bulk-messages'
  const [autoMessageSettings, setAutoMessageSettings] = useState({
    enabled: false,
    inactive_days: 7,
    message_template: 'Olá {nome}! 👋\n\nSentimos sua falta! Faz {dias} dias desde o seu último pedido.\n\nQue tal pedir algo gostoso hoje? 🍔\n\nAcesse nosso cardápio: {link}',
    min_interval_seconds: 27,
    total_sent: 0,
    auto_send_enabled: false,
    auto_send_min_interval: 45,
    auto_send_max_interval: 63,
    auto_send_status: 'idle'
  });
  const [inactiveCustomers, setInactiveCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [loadingInactive, setLoadingInactive] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0 });
  const [savingSettings, setSavingSettings] = useState(false);
  const [togglingAutoSend, setTogglingAutoSend] = useState(false);
  const [autoSendStatus, setAutoSendStatus] = useState({ enabled: false, status: 'stopped', sent_today: 0 });

  // Bulk Messages State (Disparos em Massa)
  const [bulkMessageConfig, setBulkMessageConfig] = useState({
    message1: 'Olá {nome}! 👋\n\nTemos novidades incríveis para você!\n\nConfira nosso cardápio: {link}',
    message2: 'Oi {nome}! 😊\n\nQue tal experimentar algo delicioso hoje?\n\nVeja nosso menu: {link}',
    minInterval: 27,
    maxInterval: 35,
    source: 'customers' // 'customers' or 'csv'
  });
  const [csvContacts, setCsvContacts] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [selectedBulkContacts, setSelectedBulkContacts] = useState([]);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ sent: 0, failed: 0, total: 0, currentPhone: '' });
  const [bulkLogs, setBulkLogs] = useState([]);
  
  // Scheduled Messages State (Agendamentos)
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleConfig, setScheduleConfig] = useState({
    name: '',
    scheduledDate: '',
    scheduledTime: ''
  });
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [bulkSubTab, setBulkSubTab] = useState('send'); // 'send' or 'schedules'

  useEffect(() => {
    loadCustomers();
    loadAutoMessageSettings();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (err) {
      console.error('Error loading customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAutoMessageSettings = async () => {
    try {
      const response = await axios.get(`${API}/auto-messages/settings`);
      setAutoMessageSettings(response.data);
      // Carregar clientes inativos com os dias corretos das configurações
      const days = response.data.inactive_days || 7;
      const customersResponse = await axios.get(`${API}/auto-messages/inactive-customers?days=${days}`);
      setInactiveCustomers(customersResponse.data.customers || []);
    } catch (err) {
      console.error('Error loading auto message settings:', err);
    }
  };

  const loadInactiveCustomers = async () => {
    try {
      setLoadingInactive(true);
      // Usar o valor atual de autoMessageSettings
      const days = autoMessageSettings.inactive_days || 7;
      console.log(`Carregando clientes inativos há ${days} dias...`);
      const response = await axios.get(`${API}/auto-messages/inactive-customers?days=${days}`);
      console.log(`Encontrados ${response.data.total} clientes inativos`);
      setInactiveCustomers(response.data.customers || []);
      setSelectedCustomers([]);
    } catch (err) {
      console.error('Error loading inactive customers:', err);
      alert('Erro ao carregar clientes inativos');
    } finally {
      setLoadingInactive(false);
    }
  };

  const saveAutoMessageSettings = async () => {
    try {
      setSavingSettings(true);
      await axios.post(`${API}/auto-messages/settings`, autoMessageSettings);
      alert('Configurações salvas com sucesso!');
      // Atualizar lista de clientes inativos após salvar
      loadInactiveCustomers();
    } catch (err) {
      console.error('Error saving settings:', err);
      alert(err.response?.data?.detail || 'Erro ao salvar configurações');
    } finally {
      setSavingSettings(false);
    }
  };

  // Funções de envio automático
  const loadAutoSendStatus = async () => {
    try {
      const response = await axios.get(`${API}/auto-messages/auto-send/status`);
      setAutoSendStatus(response.data);
    } catch (err) {
      console.error('Error loading auto-send status:', err);
    }
  };

  const toggleAutoSend = async () => {
    try {
      setTogglingAutoSend(true);
      const newEnabled = !autoSendStatus.enabled;
      
      const response = await axios.post(`${API}/auto-messages/auto-send/toggle`, {
        enabled: newEnabled,
        min_interval: autoMessageSettings.auto_send_min_interval || 45,
        max_interval: autoMessageSettings.auto_send_max_interval || 63
      });
      
      setAutoSendStatus({
        ...autoSendStatus,
        enabled: newEnabled,
        status: response.data.status
      });
      
      if (newEnabled) {
        alert('✅ Envio automático habilitado!\n\nAs mensagens serão enviadas automaticamente durante o horário de funcionamento.');
      } else {
        alert('🛑 Envio automático desabilitado.');
      }
    } catch (err) {
      console.error('Error toggling auto-send:', err);
      alert(err.response?.data?.detail || 'Erro ao alterar envio automático');
    } finally {
      setTogglingAutoSend(false);
    }
  };

  // Carregar status do envio automático junto com os settings
  useEffect(() => {
    loadAutoSendStatus();
    const interval = setInterval(loadAutoSendStatus, 30000); // Atualizar a cada 30s
    return () => clearInterval(interval);
  }, []);

  const handleSendMessages = async () => {
    if (selectedCustomers.length === 0) {
      alert('Selecione pelo menos um cliente');
      return;
    }

    const confirmSend = window.confirm(
      `Enviar mensagem para ${selectedCustomers.length} cliente(s)?\n\nIntervalo: ${autoMessageSettings.min_interval_seconds}s entre mensagens\nTempo estimado: ~${Math.ceil(selectedCustomers.length * autoMessageSettings.min_interval_seconds / 60)} minutos`
    );

    if (!confirmSend) return;

    try {
      setSending(true);
      setSendProgress({ sent: 0, total: selectedCustomers.length });

      const response = await axios.post(`${API}/auto-messages/send`, {
        customer_phones: selectedCustomers
      });

      setSendProgress({ sent: response.data.sent, total: selectedCustomers.length });
      
      if (response.data.sent > 0) {
        alert(`✅ ${response.data.sent} mensagem(s) enviada(s) com sucesso!${response.data.failed > 0 ? `\n❌ ${response.data.failed} falha(s)` : ''}`);
      } else {
        alert('❌ Nenhuma mensagem foi enviada. Verifique se o WhatsApp está conectado.');
      }

      // Reload inactive customers and settings
      loadInactiveCustomers();
      loadAutoMessageSettings();
      setSelectedCustomers([]);
    } catch (err) {
      console.error('Error sending messages:', err);
      alert(err.response?.data?.detail || 'Erro ao enviar mensagens');
    } finally {
      setSending(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedCustomers.length === inactiveCustomers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(inactiveCustomers.map(c => c.phone));
    }
  };

  const toggleCustomerSelection = (phone) => {
    if (selectedCustomers.includes(phone)) {
      setSelectedCustomers(selectedCustomers.filter(p => p !== phone));
    } else {
      setSelectedCustomers([...selectedCustomers, phone]);
    }
  };

  // ===== BULK MESSAGES FUNCTIONS =====
  
  const handleCsvUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setCsvFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      // Parse CSV - expect columns: phone, name (optional)
      const contacts = [];
      const header = lines[0].toLowerCase();
      const hasHeader = header.includes('telefone') || header.includes('phone') || header.includes('nome') || header.includes('name');
      
      const startIndex = hasHeader ? 1 : 0;
      
      for (let i = startIndex; i < lines.length; i++) {
        const parts = lines[i].split(/[,;]/).map(p => p.trim().replace(/"/g, ''));
        if (parts.length > 0 && parts[0]) {
          // Clean phone number
          let phone = parts[0].replace(/\D/g, '');
          if (phone.length >= 10) {
            contacts.push({
              phone: phone,
              name: parts[1] || 'Cliente'
            });
          }
        }
      }
      
      setCsvContacts(contacts);
      setSelectedBulkContacts(contacts.map(c => c.phone));
      alert(`✅ ${contacts.length} contato(s) carregado(s) do CSV`);
    };
    reader.readAsText(file);
  };

  const getRandomInterval = () => {
    const min = bulkMessageConfig.minInterval;
    const max = bulkMessageConfig.maxInterval;
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  const getRandomMessage = (contact) => {
    // Randomly choose message 1 or 2
    const template = Math.random() > 0.5 ? bulkMessageConfig.message1 : bulkMessageConfig.message2;
    
    // Get restaurant info from localStorage or use defaults
    const restaurant = JSON.parse(localStorage.getItem('restaurant') || '{}');
    const menuLink = `https://${restaurant.subdomain || 'loja'}.pedeefacil.shop`;
    
    // Replace placeholders
    return template
      .replace(/{nome}/g, contact.name || 'Cliente')
      .replace(/{link}/g, menuLink)
      .replace(/{loja}/g, restaurant.name || 'Nossa Loja');
  };

  const handleBulkSend = async () => {
    const contacts = bulkMessageConfig.source === 'csv' 
      ? csvContacts.filter(c => selectedBulkContacts.includes(c.phone))
      : customers.filter(c => selectedBulkContacts.includes(c.phone));
    
    if (contacts.length === 0) {
      alert('Selecione pelo menos um contato');
      return;
    }

    if (!bulkMessageConfig.message1.trim() || !bulkMessageConfig.message2.trim()) {
      alert('Preencha as duas mensagens');
      return;
    }

    const estimatedTime = Math.ceil(contacts.length * ((bulkMessageConfig.minInterval + bulkMessageConfig.maxInterval) / 2) / 60);
    const confirmSend = window.confirm(
      `📤 Enviar mensagens para ${contacts.length} contato(s)?\n\n` +
      `⏱️ Intervalo: ${bulkMessageConfig.minInterval}s - ${bulkMessageConfig.maxInterval}s\n` +
      `📊 Tempo estimado: ~${estimatedTime} minutos\n\n` +
      `As mensagens serão enviadas em rodízio para evitar padrões.`
    );

    if (!confirmSend) return;

    setBulkSending(true);
    setBulkProgress({ sent: 0, failed: 0, total: contacts.length, currentPhone: '' });
    setBulkLogs([]);

    try {
      const response = await axios.post(`${API}/bulk-messages/send`, {
        contacts: contacts.map(c => ({ phone: c.phone, name: c.name || 'Cliente' })),
        message1: bulkMessageConfig.message1,
        message2: bulkMessageConfig.message2,
        minInterval: bulkMessageConfig.minInterval,
        maxInterval: bulkMessageConfig.maxInterval
      });

      setBulkProgress({
        sent: response.data.sent,
        failed: response.data.failed,
        total: contacts.length,
        currentPhone: 'Concluído'
      });

      if (response.data.sent > 0) {
        alert(`✅ Envio concluído!\n\n📤 Enviadas: ${response.data.sent}\n❌ Falhas: ${response.data.failed}`);
      } else {
        alert('❌ Nenhuma mensagem foi enviada. Verifique se o WhatsApp está conectado.');
      }

      setSelectedBulkContacts([]);
    } catch (err) {
      console.error('Error sending bulk messages:', err);
      alert(err.response?.data?.detail || 'Erro ao enviar mensagens');
    } finally {
      setBulkSending(false);
    }
  };

  // ====== SCHEDULED MESSAGES FUNCTIONS ======
  const loadScheduledMessages = async () => {
    try {
      setLoadingSchedules(true);
      const response = await axios.get(`${API}/bulk-messages/schedules`);
      setScheduledMessages(response.data || []);
    } catch (err) {
      console.error('Error loading scheduled messages:', err);
    } finally {
      setLoadingSchedules(false);
    }
  };

  const handleScheduleBulkSend = async () => {
    const contacts = bulkMessageConfig.source === 'csv' 
      ? csvContacts.filter(c => selectedBulkContacts.includes(c.phone))
      : customers.filter(c => selectedBulkContacts.includes(c.phone));
    
    if (contacts.length === 0) {
      alert('Selecione pelo menos um contato');
      return;
    }

    if (!bulkMessageConfig.message1.trim() || !bulkMessageConfig.message2.trim()) {
      alert('Preencha as duas mensagens');
      return;
    }

    if (!scheduleConfig.scheduledDate || !scheduleConfig.scheduledTime) {
      alert('Selecione a data e hora do agendamento');
      return;
    }

    // Combine date and time
    const scheduledDatetime = `${scheduleConfig.scheduledDate}T${scheduleConfig.scheduledTime}:00`;
    
    try {
      setSavingSchedule(true);
      const response = await axios.post(`${API}/bulk-messages/schedule`, {
        contacts: contacts.map(c => ({ phone: c.phone, name: c.name || 'Cliente' })),
        message1: bulkMessageConfig.message1,
        message2: bulkMessageConfig.message2,
        minInterval: bulkMessageConfig.minInterval,
        maxInterval: bulkMessageConfig.maxInterval,
        scheduled_datetime: scheduledDatetime,
        name: scheduleConfig.name || `Disparo de ${new Date().toLocaleDateString('pt-BR')}`
      });

      alert(`✅ ${response.data.message}\n\n📊 ${contacts.length} contato(s) agendados`);
      
      setShowScheduleModal(false);
      setScheduleConfig({ name: '', scheduledDate: '', scheduledTime: '' });
      setSelectedBulkContacts([]);
      loadScheduledMessages();
    } catch (err) {
      console.error('Error scheduling bulk messages:', err);
      alert(err.response?.data?.detail || 'Erro ao agendar mensagens');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleCancelSchedule = async (scheduleId) => {
    if (!window.confirm('Deseja realmente cancelar este agendamento?')) return;
    
    try {
      await axios.delete(`${API}/bulk-messages/schedules/${scheduleId}`);
      alert('Agendamento cancelado com sucesso!');
      loadScheduledMessages();
    } catch (err) {
      console.error('Error cancelling schedule:', err);
      alert(err.response?.data?.detail || 'Erro ao cancelar agendamento');
    }
  };

  // Load schedules when bulk-messages tab is active
  useEffect(() => {
    if (activeTab === 'bulk-messages') {
      loadScheduledMessages();
    }
  }, [activeTab]);

  const toggleBulkSelectAll = () => {
    const contacts = bulkMessageConfig.source === 'csv' ? csvContacts : customers;
    if (selectedBulkContacts.length === contacts.length) {
      setSelectedBulkContacts([]);
    } else {
      setSelectedBulkContacts(contacts.map(c => c.phone));
    }
  };

  const toggleBulkContactSelection = (phone) => {
    if (selectedBulkContacts.includes(phone)) {
      setSelectedBulkContacts(selectedBulkContacts.filter(p => p !== phone));
    } else {
      setSelectedBulkContacts([...selectedBulkContacts, phone]);
    }
  };

  const handleExport = async (format = 'csv') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/customers/export?format=${format}`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const extension = format.toLowerCase();
      link.setAttribute('download', `clientes_${new Date().toISOString().split('T')[0]}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting customers:', error);
      alert('Erro ao exportar clientes. Verifique se você está logado.');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Selecione um arquivo CSV ou XLSX');
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`${API}/customers/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(response.data);
      setImportFile(null);
      
      // Reload customers
      loadCustomers();
      
      setTimeout(() => {
        setIsImportModalOpen(false);
        setImportResult(null);
      }, 3000);
    } catch (error) {
      console.error('Error importing customers:', error);
      alert('Erro ao importar clientes: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = `telefone,nome,total_pedidos,total_gasto,primeiro_pedido,ultimo_pedido
11999887766,João Silva,5,150.00,2024-01-15,2024-10-18
11888776655,Maria Santos,3,89.50,2024-02-20,2024-09-25
11777665544,Pedro Costa,8,245.90,2024-01-10,2024-10-19

INSTRUÇÕES DE PREENCHIMENTO:
=============================
1. NÃO remova a primeira linha (cabeçalho)
2. Telefone: número com DDD (obrigatório)
3. Nome: nome completo do cliente (obrigatório)
4. Total_pedidos: número de pedidos realizados (opcional)
5. Total_gasto: valor total gasto pelo cliente (opcional)
6. Primeiro_pedido: data do primeiro pedido no formato AAAA-MM-DD (opcional)
7. Ultimo_pedido: data do último pedido no formato AAAA-MM-DD (opcional)

NOTA: A importação irá atualizar os nomes dos clientes existentes (por telefone)
`;
    
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'template_clientes.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  const filteredCustomers = customers.filter(customer => 
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone.includes(searchTerm)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">Clientes</h2>
          <p className="text-gray-400">Gerencie sua base de clientes</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-white">{customers.length}</div>
          <div className="text-sm text-gray-400">Total de Clientes</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 sm:gap-2 border-b border-gray-700 pb-2 overflow-x-auto scrollbar-hide">
        <button
          onClick={() => setActiveTab('customers')}
          className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-t-lg font-medium transition-colors whitespace-nowrap text-xs sm:text-sm ${
            activeTab === 'customers'
              ? 'bg-gray-800 text-white border-b-2 border-blue-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <User size={16} className="flex-shrink-0" />
          <span className="hidden sm:inline">Lista de Clientes</span>
          <span className="sm:hidden">Clientes</span>
        </button>
        <button
          onClick={() => {
            setActiveTab('auto-messages');
            // Sempre carregar a lista ao abrir a aba para garantir dados atualizados
            loadInactiveCustomers();
          }}
          className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-t-lg font-medium transition-colors whitespace-nowrap text-xs sm:text-sm ${
            activeTab === 'auto-messages'
              ? 'bg-gray-800 text-white border-b-2 border-green-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Clock size={16} className="flex-shrink-0" />
          <span className="hidden sm:inline">Clientes Inativos</span>
          <span className="sm:hidden">Inativos</span>
        </button>
        <button
          onClick={() => setActiveTab('bulk-messages')}
          className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-t-lg font-medium transition-colors whitespace-nowrap text-xs sm:text-sm ${
            activeTab === 'bulk-messages'
              ? 'bg-gray-800 text-white border-b-2 border-purple-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Send size={16} className="flex-shrink-0" />
          <span className="hidden sm:inline">Disparos em Massa</span>
          <span className="sm:hidden">Disparos</span>
        </button>
      </div>

      {activeTab === 'customers' ? (
        <>
          {/* Import/Export Menu - Optimized */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center gap-2 px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-xl text-gray-400 hover:text-gray-300 hover:bg-gray-800 hover:border-gray-600 transition-colors"
            data-testid="import-export-customers-menu-button"
            title="Importar/Exportar"
          >
            <MoreVertical size={18} />
            <span className="text-sm font-medium">Opções</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-gray-900 border-gray-700 w-56">
          <DropdownMenuItem 
            onClick={() => handleExport('csv')}
            className="text-gray-300 hover:bg-gray-800 cursor-pointer"
            data-testid="export-customers-csv-button"
          >
            <Download size={16} className="mr-2" />
            Exportar como CSV
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => handleExport('xlsx')}
            className="text-gray-300 hover:bg-gray-800 cursor-pointer"
            data-testid="export-customers-xlsx-button"
          >
            <Download size={16} className="mr-2" />
            Exportar como XLSX
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => setIsImportModalOpen(true)}
            className="text-gray-300 hover:bg-gray-800 cursor-pointer"
            data-testid="import-customers-button"
          >
            <Upload size={16} className="mr-2" />
            Importar CSV/XLSX
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={downloadTemplate}
            className="text-gray-300 hover:bg-gray-800 cursor-pointer"
            data-testid="template-customers-button"
          >
            <FileText size={16} className="mr-2" />
            Baixar Template
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Search Bar */}
      <div className="glassmorphism-card rounded-2xl p-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar por nome ou telefone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors"
            data-testid="customer-search-input"
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="glassmorphism-card rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
              <User className="text-white" size={24} />
            </div>
            <div>
              <div className="text-sm text-gray-400">Total de Clientes</div>
              <div className="text-2xl font-bold text-white">{customers.length}</div>
            </div>
          </div>
        </div>

        <div className="glassmorphism-card rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
              <ShoppingCart className="text-white" size={24} />
            </div>
            <div>
              <div className="text-sm text-gray-400">Total de Pedidos</div>
              <div className="text-2xl font-bold text-white">
                {customers.reduce((sum, c) => sum + c.total_orders, 0)}
              </div>
            </div>
          </div>
        </div>

        <div className="glassmorphism-card rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center">
              <DollarSign className="text-white" size={24} />
            </div>
            <div>
              <div className="text-sm text-gray-400">Receita Total</div>
              <div className="text-2xl font-bold text-white">
                R$ {customers.reduce((sum, c) => sum + c.total_spent, 0).toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customers Table */}
      <div className="glassmorphism-card rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Cliente</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Telefone</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Pedidos</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Total Gasto</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Último Pedido</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Dias sem Comprar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                    Carregando clientes...
                  </td>
                </tr>
              ) : filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                    {searchTerm ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado ainda'}
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer, index) => (
                  <tr 
                    key={index} 
                    className="hover:bg-gray-800/30 transition-colors"
                    data-testid={`customer-row-${index}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                          <span className="text-white font-semibold">
                            {customer.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="text-white font-medium">{customer.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-300">{customer.phone}</td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-500/20 text-blue-400">
                        {customer.total_orders}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-green-400 font-semibold">
                        R$ {customer.total_spent.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-gray-400 text-sm">
                      {formatDate(customer.last_order)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        customer.days_inactive >= 30 
                          ? 'bg-red-500/20 text-red-400' 
                          : customer.days_inactive >= 7 
                            ? 'bg-amber-500/20 text-amber-400'
                            : 'bg-green-500/20 text-green-400'
                      }`}>
                        {customer.days_inactive || 0} dias
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Import Modal */}
      <Dialog open={isImportModalOpen} onOpenChange={setIsImportModalOpen}>
        <DialogContent className="glass-modal max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Importar Clientes</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Instructions */}
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-blue-400 mb-2">📋 Instruções:</h3>
              <ul className="text-xs text-gray-300 space-y-1">
                <li>• Baixe o template CSV para ver o formato</li>
                <li>• Preencha com os dados dos clientes</li>
                <li>• Faça upload do arquivo CSV ou XLSX preenchido</li>
                <li>• A importação irá atualizar nomes de clientes existentes</li>
              </ul>
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Selecionar Arquivo CSV ou XLSX
              </label>
              <input
                type="file"
                accept=".csv,.xlsx"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600 cursor-pointer"
                data-testid="import-customers-file-input"
              />
              {importFile && (
                <p className="text-sm text-green-400 mt-2">
                  ✓ Arquivo selecionado: {importFile.name}
                </p>
              )}
            </div>

            {/* Template Button */}
            <button
              onClick={downloadTemplate}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-gray-300 hover:bg-gray-800 hover:border-gray-600 transition-colors"
            >
              <FileText size={18} />
              <span>Baixar Template de Exemplo</span>
            </button>

            {/* Import Result */}
            {importResult && (
              <div className="space-y-3">
                <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-green-400 mb-2">✓ Importação Concluída!</h3>
                  <ul className="text-xs text-gray-300 space-y-1">
                    <li>• {importResult.created} novos clientes</li>
                    <li>• {importResult.updated} clientes atualizados</li>
                  </ul>
                </div>
                
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                    <p className="text-xs font-semibold text-red-400 mb-2">❌ Erros ({importResult.errors.length}):</p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {importResult.errors.map((err, i) => (
                        <p key={i} className="text-xs text-red-300">• {err}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsImportModalOpen(false);
                  setImportFile(null);
                  setImportResult(null);
                }}
                className="flex-1 px-4 py-3 text-base text-gray-400 hover:text-white transition-colors rounded-xl border border-gray-700 hover:border-gray-600"
                disabled={importing}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleImport}
                disabled={!importFile || importing}
                className="flex-1 px-4 py-3 text-base bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:from-indigo-600 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                data-testid="confirm-import-customers-button"
              >
                {importing ? 'Importando...' : 'Importar'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
        </>
      ) : activeTab === 'auto-messages' ? (
        /* Auto Messages Tab */
        <div className="space-y-6">
          {/* Settings Card */}
          <div className="glassmorphism-card rounded-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Settings size={24} />
              Configurações de Disparo
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Days of Inactivity */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Dias sem comprar
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={autoMessageSettings.inactive_days}
                    onChange={(e) => setAutoMessageSettings({
                      ...autoMessageSettings,
                      inactive_days: parseInt(e.target.value) || 7
                    })}
                    className="flex-1 px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white"
                    data-testid="inactive-days-input"
                  />
                  <button
                    onClick={loadInactiveCustomers}
                    disabled={loadingInactive}
                    className="px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 transition-all font-medium whitespace-nowrap"
                    data-testid="search-inactive-button"
                  >
                    {loadingInactive ? 'Buscando...' : '🔍 Buscar'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Clientes que não compram há X dias receberão a mensagem
                </p>
              </div>

              {/* Min Interval */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Intervalo entre mensagens (segundos)
                </label>
                <input
                  type="number"
                  min="27"
                  max="120"
                  value={autoMessageSettings.min_interval_seconds}
                  onChange={(e) => setAutoMessageSettings({
                    ...autoMessageSettings,
                    min_interval_seconds: Math.max(27, parseInt(e.target.value) || 27)
                  })}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Mínimo: 27 segundos (segurança do WhatsApp)
                </p>
              </div>
            </div>

            {/* Message Template */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Mensagem Personalizada
              </label>
              <textarea
                value={autoMessageSettings.message_template}
                onChange={(e) => setAutoMessageSettings({
                  ...autoMessageSettings,
                  message_template: e.target.value
                })}
                rows={5}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white resize-none"
                placeholder="Digite sua mensagem..."
                data-testid="message-template-input"
              />
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">{'{nome}'} = Nome do cliente</span>
                <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">{'{dias}'} = Dias sem comprar</span>
                <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">{'{link}'} = Link do cardápio</span>
                <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">{'{loja}'} = Nome da loja</span>
              </div>
            </div>

            {/* ========== ENVIO AUTOMÁTICO ========== */}
            <div className="mt-8 p-5 bg-gradient-to-br from-emerald-900/30 to-teal-900/30 border border-emerald-500/30 rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-lg font-semibold text-emerald-400 flex items-center gap-2">
                    🤖 Envio Automático
                    {autoSendStatus.status === 'running' && (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full animate-pulse">
                        Ativo
                      </span>
                    )}
                  </h4>
                  <p className="text-sm text-gray-400 mt-1">
                    Envia mensagens automaticamente durante o horário de funcionamento
                  </p>
                </div>
                <button
                  onClick={toggleAutoSend}
                  disabled={togglingAutoSend}
                  className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
                    autoSendStatus.enabled ? 'bg-emerald-500' : 'bg-gray-600'
                  }`}
                  data-testid="auto-send-toggle"
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                      autoSendStatus.enabled ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              {autoSendStatus.enabled && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                  <div className="bg-gray-800/50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-emerald-400">{autoSendStatus.sent_today || 0}</p>
                    <p className="text-xs text-gray-400">Enviadas hoje</p>
                  </div>
                  <div className="bg-gray-800/50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-blue-400">{autoMessageSettings.auto_send_min_interval || 45}s</p>
                    <p className="text-xs text-gray-400">Intervalo mín.</p>
                  </div>
                  <div className="bg-gray-800/50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-blue-400">{autoMessageSettings.auto_send_max_interval || 63}s</p>
                    <p className="text-xs text-gray-400">Intervalo máx.</p>
                  </div>
                  <div className="bg-gray-800/50 rounded-xl p-3 text-center">
                    <p className={`text-lg font-bold ${autoSendStatus.status === 'running' ? 'text-green-400' : 'text-gray-400'}`}>
                      {autoSendStatus.status === 'running' ? '🟢 Rodando' : '⏸️ Parado'}
                    </p>
                    <p className="text-xs text-gray-400">Status</p>
                  </div>
                </div>
              )}
              
              <div className="mt-4 p-3 bg-gray-800/30 rounded-xl">
                <p className="text-xs text-gray-400">
                  <strong className="text-emerald-400">Como funciona:</strong> O sistema enviará mensagens automaticamente para clientes inativos 
                  durante o horário de funcionamento da loja, respeitando um intervalo aleatório de {autoMessageSettings.auto_send_min_interval || 45}-{autoMessageSettings.auto_send_max_interval || 63} segundos 
                  entre cada mensagem para evitar bloqueios do WhatsApp.
                </p>
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-400">
                Total de mensagens enviadas: <span className="text-green-400 font-bold">{autoMessageSettings.total_sent || 0}</span>
              </div>
              <button
                onClick={saveAutoMessageSettings}
                disabled={savingSettings}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 disabled:opacity-50 transition-all font-medium"
              >
                {savingSettings ? 'Salvando...' : 'Salvar Configurações'}
              </button>
            </div>
          </div>

          {/* Inactive Customers */}
          <div className="glassmorphism-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Clock size={24} />
                Clientes Inativos ({inactiveCustomers.length})
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={loadInactiveCustomers}
                  disabled={loadingInactive}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 transition-colors"
                >
                  {loadingInactive ? 'Buscando...' : 'Atualizar Lista'}
                </button>
                {selectedCustomers.length > 0 && (
                  <button
                    onClick={handleSendMessages}
                    disabled={sending}
                    className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 disabled:opacity-50 transition-all font-medium flex items-center gap-2"
                    data-testid="send-messages-button"
                  >
                    <Send size={18} />
                    {sending ? `Enviando... (${sendProgress.sent}/${sendProgress.total})` : `Enviar para ${selectedCustomers.length} cliente(s)`}
                  </button>
                )}
              </div>
            </div>

            {/* Select All */}
            {inactiveCustomers.length > 0 && (
              <div className="mb-4 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedCustomers.length === inactiveCustomers.length && inactiveCustomers.length > 0}
                  onChange={toggleSelectAll}
                  className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-blue-500"
                />
                <span className="text-gray-300">Selecionar todos</span>
              </div>
            )}

            {/* Customer List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {loadingInactive ? (
                <div className="text-center py-8 text-gray-400">Buscando clientes inativos...</div>
              ) : inactiveCustomers.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Clock size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Nenhum cliente inativo há {autoMessageSettings.inactive_days} dias</p>
                  <p className="text-sm mt-2">Seus clientes estão ativos! 🎉</p>
                </div>
              ) : (
                inactiveCustomers.map((customer, index) => (
                  <div
                    key={index}
                    onClick={() => toggleCustomerSelection(customer.phone)}
                    className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-colors ${
                      selectedCustomers.includes(customer.phone)
                        ? 'bg-blue-500/20 border border-blue-500/50'
                        : 'bg-gray-800/50 hover:bg-gray-800'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCustomers.includes(customer.phone)}
                      onChange={() => {}}
                      className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-blue-500"
                    />
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                      <span className="text-white font-semibold">
                        {(customer.name || 'C').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-medium">{customer.name || 'Cliente'}</div>
                      <div className="text-sm text-gray-400">{customer.phone}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-orange-400 font-bold">{customer.days_inactive} dias</div>
                      <div className="text-xs text-gray-500">sem comprar</div>
                    </div>
                    <div className="text-right">
                      <div className="text-gray-300">{customer.total_orders || 0} pedidos</div>
                      <div className="text-xs text-green-400">R$ {(customer.total_spent || 0).toFixed(2)}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      ) : activeTab === 'bulk-messages' && (
        /* Campanhas de WhatsApp - Sistema Completo */
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg md:text-2xl font-bold text-white flex items-center gap-2 md:gap-3">
              <Send size={20} className="md:hidden flex-shrink-0" />
              <Send size={28} className="hidden md:block flex-shrink-0" />
              Campanhas de WhatsApp
            </h2>
          </div>
          
          <CampaignsPanel 
            customers={customers}
            onRefreshCustomers={loadCustomers}
          />
        </div>
      )}
    </div>
  );
};
export default CustomersContent;
